import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { SubscriptionTiersService } from '@galaxy/partner';
import { LoopOptions } from '@vendasta/sales-common';
import { PostIdentifier } from '@vendasta/the-loop';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AppConfig, AppConfigService } from '../app-config.service';
import { ContractStatusesService } from '../billing/contract/status/contract-statuses.service';
import { UnifiedTOSDialogComponent } from '../common/tos-unified/unified-tos-dialog.component';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { getImageSrc } from '../core/images';
import { PartnerService } from '../core/partner.service';
import { TrialStatus } from '../core/partner/partner-trial-status';
import { UnifiedTOSService } from '../core/tos-unified.service';
import { UserDismissalService } from '../core/user-dismissal.service';
import { HighlightsService, SimpleUrlHighlight } from '../highlights';
import { PartnerTrialStoreService } from '../partner-trial';
import { CardConfig, DashboardService } from './dashboard.service';

declare let window: any;
const SHOULD_SHOW_TOS_MODAL = 'ShouldShowTOSModal';

interface WidgetUiState {
  isLastTab: boolean;
  isFirstTab: boolean;
  atNestedTab: boolean;
}

@Component({
  styleUrls: ['dashboard.component.scss'],
  templateUrl: './dashboard.component.html',
  providers: [ContractStatusesService],
})
export class DashboardComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  widgetState = 'inactive';
  widgetSelectedIndex$$: BehaviorSubject<number> = new BehaviorSubject(0);
  widgetSelectedIndex$: Observable<number>;
  nestedSelectedIndex$$: BehaviorSubject<number> = new BehaviorSubject(0);
  nestedSelectedIndex$: Observable<number>;
  WIDGET_INDEX = 'widgetIndex';
  TAB_INDEX = 'tabIndex';
  MAX_WIDGET_INDEX = 5;
  MAX_TAB_INDEX = 1;
  MILLISECOND_FOR_A_WEEK = 604800000;
  highlights$: Observable<SimpleUrlHighlight[]>;
  hasAccessToSalesAndSuccessCenter$: Observable<boolean>;
  hasAccessToProspects$: Observable<boolean>;
  hasAccessToTheLoop$: Observable<boolean>;
  isFree$: Observable<boolean>;
  widgetUiState$: Observable<WidgetUiState>;

  cardConfig$: Observable<CardConfig[]>;
  showCards$: Observable<boolean>;

  revampedDashboardStatCardsClass$: Observable<string>;

  config: AppConfig;

  partnerSuspended$: Observable<boolean>;

  tosDialog: MatDialogRef<UnifiedTOSDialogComponent>;
  trialExpiringSoon$: Observable<boolean>;
  trialExpired$: Observable<boolean>;
  trialDaysRemaining$: Observable<string>;
  trialExists$: Observable<boolean>;
  bannerDismissed: boolean;
  accountExecutiveEmail: string;
  staleBanner$: Observable<boolean>;
  getImageSrc = getImageSrc;

  loopOptions: LoopOptions;

  needsOnboardingUseCaseSelection$: Observable<boolean>;

  constructor(
    private router: Router,
    private dashboardService: DashboardService,
    private contractStatusService: ContractStatusesService,
    private partnerService: PartnerService,
    private subscriptionTiersService: SubscriptionTiersService,
    private trialService: PartnerTrialStoreService,
    private userDismissal: UserDismissalService,
    private appConfigService: AppConfigService,
    @Inject(StubAccessService) private access: AccessService,
    public dialog: MatDialog,
    private highlightsService: HighlightsService,
    private tosService: UnifiedTOSService,
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
  ) {}

  ngOnInit(): void {
    this.config = this.appConfigService.config;
    this.loopOptions = {
      forcedFilters: {
        marketIds: this.config.accessibleMarketIds,
      },
      compact: true,
      pageSize: 3,
      instanceId: 'dashboard-compact',
    };
    this.bannerDismissed = this.getCookie('trialExpiring') === 'dismissed';
    this.accountExecutiveEmail = this.config.accountExecutive;
    this.trialExists$ = this.trialService.partnerHasTrial$;
    this.trialExpiringSoon$ = this.trialService.partnerTrial$.pipe(map((t) => t.expiringSoon));
    this.trialExpired$ = this.trialService.partnerTrial$.pipe(map((t) => t.status === TrialStatus.EXPIRED));
    this.trialDaysRemaining$ = this.trialService.partnerTrial$.pipe(
      map((t) => this.formatRemainingTrialDays(t.remainingDays)),
    );
    this.staleBanner$ = combineLatest([
      this.trialService.partnerTrial$,
      this.partnerService.subscriptionTier$,
      this.subscriptionTiersService.subscriptionTierList$,
    ]).pipe(
      map(([trialSubscription, currentSubscription, subscriptionTiers]) => {
        // get the rank of the trial subscription
        if (trialSubscription.subscriptionTierId) {
          const trialSubscriptionTier = subscriptionTiers.find((st) => st.id === trialSubscription.subscriptionTierId);
          return trialSubscriptionTier.rank <= currentSubscription.rank || trialSubscription.expiredOver30DaysAgo;
        }
        return true;
      }),
      startWith(true),
    );
    this.subscriptions.push(
      this.tosService.tosDataLoaded$.subscribe((isReady) => {
        if (isReady && sessionStorage.getItem(SHOULD_SHOW_TOS_MODAL) !== 'no') {
          if (!this.tosService.hasAcceptedTOS && !this.tosService.isClassicSubscription) {
            this.openTOSDialog();
            sessionStorage.setItem(SHOULD_SHOW_TOS_MODAL, 'no');
          }
        }
      }),
    );

    this.userDismissal.checkTutorialWidget();
    this.subscriptions.push(
      this.userDismissal.ShowTutorialWidget.subscribe((showWidget: boolean) => {
        const widgetIndexStr = this.getCookie(this.WIDGET_INDEX);
        this.widgetSelectedIndex$$.next(widgetIndexStr ? +widgetIndexStr : 0);
        const tabIndexStr = this.getCookie(this.TAB_INDEX);
        this.nestedSelectedIndex$$.next(tabIndexStr ? +tabIndexStr : 0);
        this.widgetState = showWidget ? 'active' : 'inactive';
        if (showWidget) {
          this.loadGTM();
        }
      }),
    );

    this.partnerSuspended$ = this.contractStatusService.getStatusForPartner(this.partnerService.partnerId).pipe(
      map((contractStatus) => {
        const key = 'status';
        return contractStatus[key] === 'delinquent';
      }),
    );

    this.cardConfig$ = this.dashboardService.getCardConfig$();
    this.showCards$ = this.cardConfig$.pipe(map((cc) => cc.filter((c) => c.show).length !== 0));

    this.hasAccessToSalesAndSuccessCenter$ = this.access.hasAccessToFeature(Feature.salesAndSuccessCenterAccess);
    this.hasAccessToProspects$ = this.access.hasAccessToFeature(Feature.prospects);
    this.hasAccessToTheLoop$ = this.access.hasAccessToFeature(Feature.theLoop);
    this.revampedDashboardStatCardsClass$ = this.hasAccessToSalesAndSuccessCenter$.pipe(
      map((hasAccess) => (hasAccess ? 'col-xs-4' : 'col-xs-6')),
      startWith('col-xs-4'),
    );

    this.isFree$ = this.dashboardService.getIsFree();

    this.highlights$ = this.highlightsService.getHighlights();

    this.widgetSelectedIndex$ = this.widgetSelectedIndex$$.asObservable();
    this.nestedSelectedIndex$ = this.nestedSelectedIndex$$.asObservable();
    this.widgetUiState$ = combineLatest([this.widgetSelectedIndex$, this.nestedSelectedIndex$]).pipe(
      map(([widgetSelectedIndex, nestedSelectedIndex]) => ({
        isLastTab: widgetSelectedIndex >= this.MAX_WIDGET_INDEX && nestedSelectedIndex === this.MAX_TAB_INDEX,
        isFirstTab: widgetSelectedIndex === 0,
        atNestedTab: widgetSelectedIndex >= this.MAX_WIDGET_INDEX,
      })),
    );
  }

  public openTOSDialog(): void {
    if (this.tosDialog) {
      return;
    }
    this.tosDialog = this.dialog.open(UnifiedTOSDialogComponent, UnifiedTOSDialogComponent.config());
    this.subscriptions.push(this.tosDialog.afterClosed().subscribe(() => (this.tosDialog = null)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  mailBilling(): void {
    window.open('mailto:billing@vendasta.com');
  }

  loadGTM(): void {
    const page_index = this.widgetSelectedIndex$$.getValue() + this.nestedSelectedIndex$$.getValue();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'widgetSwitchTab',
      widgetIndex: page_index,
      forward: true,
    });
  }

  getCookie(key: string): string {
    const keyValue = document.cookie.match('(^|;) ?' + key + '=([^;]*)(;|$)');
    return keyValue ? keyValue[2] : null;
  }

  setCookie(key: string, value: string, expiry: number): void {
    const expires: Date = new Date();
    expires.setTime(expires.getTime() + expiry);
    document.cookie = key + '=' + value + ';path=/' + ';expires=' + expires.toUTCString() + ';secure';
  }

  formatRemainingTrialDays(remainingDays: number): string {
    if (remainingDays <= 0) {
      return 'Trial Expired';
    }
    return ' ' + remainingDays + (remainingDays === 1 ? ' day' : ' days');
  }

  hideBanner(): void {
    this.setCookie('trialExpiring', 'dismissed', this.MILLISECOND_FOR_A_WEEK * this.MILLISECOND_FOR_A_WEEK);
    this.bannerDismissed = true;
  }

  upgradeSubscription(): void {
    this.goToLink('/upgrade');
  }

  goToLink(path: string): void {
    if (path.indexOf('http') >= 0) {
      window.open(path, '_blank');
    } else {
      this.router.navigateByUrl(path);
    }
  }

  goToLoopPost(id: PostIdentifier): void {
    this.goToLink('/salesperson-activity/type/' + id.typeId + '?postId=' + id.postId);
  }

  manageTeamMembers(): void {
    this.router.navigateByUrl('/my-team');
  }
}
